import { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, AlertCircle, Info, CheckCircle, X } from 'lucide-react';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import './ConfirmationModal.css';

export type ConfirmationVariant = 'danger' | 'warning' | 'info' | 'success';

export interface ConfirmationModalProps {
  /** Contrôle l'affichage de la modale */
  isOpen: boolean;
  /** Titre de la modale */
  title: string;
  /** Message de confirmation */
  message: string | React.ReactNode;
  /** Texte du bouton de confirmation */
  confirmText?: string;
  /** Texte du bouton d'annulation */
  cancelText?: string;
  /** Variante visuelle de la modale */
  variant?: ConfirmationVariant;
  /** Afficher un état de chargement sur le bouton de confirmation */
  isLoading?: boolean;
  /** Callback appelé lors de la confirmation */
  onConfirm: () => void | Promise<void>;
  /** Callback appelé lors de l'annulation */
  onCancel: () => void;
  /** Désactiver la fermeture via Escape ou clic sur l'overlay */
  disableCloseOnOverlay?: boolean;
}

const variantConfig = {
  danger: {
    icon: AlertTriangle,
    iconColor: 'var(--color-error)',
    iconBg: 'var(--color-error-light)',
    buttonClass: 'confirmation-btn-danger'
  },
  warning: {
    icon: AlertCircle,
    iconColor: 'var(--color-warning)',
    iconBg: 'var(--color-warning-light)',
    buttonClass: 'confirmation-btn-warning'
  },
  info: {
    icon: Info,
    iconColor: 'var(--color-info)',
    iconBg: 'var(--color-info-light)',
    buttonClass: 'confirmation-btn-info'
  },
  success: {
    icon: CheckCircle,
    iconColor: 'var(--color-success)',
    iconBg: 'var(--color-success-light)',
    buttonClass: 'confirmation-btn-success'
  }
};

export function ConfirmationModal({
  isOpen,
  title,
  message,
  confirmText = 'Confirmer',
  cancelText = 'Annuler',
  variant = 'info',
  isLoading = false,
  onConfirm,
  onCancel,
  disableCloseOnOverlay = false
}: ConfirmationModalProps) {
  const modalRef = useFocusTrap<HTMLDivElement>(isOpen);
  const config = variantConfig[variant];
  const IconComponent = config.icon;

  // Gestion de la touche Escape
  const handleEscape = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape' && !disableCloseOnOverlay && !isLoading) {
      onCancel();
    }
  }, [disableCloseOnOverlay, isLoading, onCancel]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Empêcher le scroll du body
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  // Gestion du clic sur l'overlay
  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget && !disableCloseOnOverlay && !isLoading) {
      onCancel();
    }
  };

  // Gestion de la confirmation
  const handleConfirm = async () => {
    if (isLoading) return;
    await onConfirm();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="confirmation-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleOverlayClick}
          role="presentation"
        >
          <motion.div
            ref={modalRef}
            className="confirmation-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirmation-title"
            aria-describedby="confirmation-message"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          >
            {/* Header avec icône */}
            <div className="confirmation-modal-header">
              <div
                className="confirmation-modal-icon"
                style={{
                  backgroundColor: config.iconBg,
                  color: config.iconColor
                }}
              >
                <IconComponent size={24} aria-hidden="true" />
              </div>
              <button
                type="button"
                className="confirmation-modal-close"
                onClick={onCancel}
                disabled={isLoading}
                aria-label="Fermer"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>

            {/* Contenu */}
            <div className="confirmation-modal-content">
              <h2 id="confirmation-title" className="confirmation-modal-title">
                {title}
              </h2>
              <div id="confirmation-message" className="confirmation-modal-message">
                {typeof message === 'string' ? <p>{message}</p> : message}
              </div>
            </div>

            {/* Actions */}
            <div className="confirmation-modal-actions">
              <button
                type="button"
                className="confirmation-btn confirmation-btn-cancel"
                onClick={onCancel}
                disabled={isLoading}
              >
                {cancelText}
              </button>
              <button
                type="button"
                className={`confirmation-btn ${config.buttonClass}`}
                onClick={handleConfirm}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="confirmation-spinner" aria-hidden="true" />
                    <span>Chargement...</span>
                  </>
                ) : (
                  confirmText
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Hook pour utiliser ConfirmationModal de manière impérative
export interface ConfirmOptions {
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmationVariant;
}

export default ConfirmationModal;
